import type { Handler, Match, RouteHooks } from "navigo";
import Navigo from "navigo";
import type { ChildDom } from "vanjs-core";
import { setPageContent } from ".";
import type { Api } from "./api";
import { loadingScreen } from "./loading-screen";
import { scrollToTop, type ViewComponent } from "./van/view-component";

export class Router {
	#jumpToClosestParent(path: string): void {
		if (path === "") {
			window.location.href = window.location.origin;
			return;
		}

		const parent = path.split("/").slice(0, -1).join("/");
		const match = this.#navigo.match(parent);
		if (!match) {
			this.#jumpToClosestParent(parent);
			return;
		}

		this.jump(parent);
	}

	down(target: string): void {
		target = window.location.pathname + target;
		this.jump(target);
	}

	jump(target: string): void {
		this.#navigo.navigate(target);
	}

	registerRoute(path: string, Route: RouteConstructor): void {
		let route: Route | undefined;

		const routeHandler = async (
			match: Match | undefined,
		): Promise<void> => {
			if (!this.routeContext) {
				throw "missing route context";
			}

			if (!match) {
				throw "missing route match";
			}

			if (!route) {
				route = new Route(this.routeContext);
				this.#navigo.addLeaveHook(
					path,
					(done: (b: boolean) => void) => {
						route?.onLeave?.();
						done(true);
					},
				);
			}

			// Content must be attached to DOM before calling refresh
			setPageContent(route.ChildDom);

			if (route?.refresh) {
				// For some reason this timeout is needed before calling refresh
				// for the changes to display. Using RAF did not work.
				const refresh = new Promise((resolve) =>
					setTimeout(
						() => route?.refresh?.(match).then(resolve),
						200,
					),
				);

				// We need to wait for the refresh before scrolling to top
				await loadingScreen.waitFor(refresh);
			}

			scrollToTop();
		};

		this.#navigo.on(path, routeHandler);
	}

	registerRouteDEPRECATED(
		path: string,
		page: Handler,
		hooks?: RouteHooks,
	): void {
		const routeHandler: Handler = (match: Match | undefined) => {
			const prepareRoute = new Promise((resolve) => {
				page(match);
				resolve(true);
			});
			loadingScreen.waitFor(prepareRoute);
		};
		this.#navigo.on(path, routeHandler, hooks);
	}

	resolve(): void {
		this.#navigo.resolve();
	}

	up(): void {
		const hasHistory =
			window.history.length > 1 &&
			document.referrer.startsWith(window.location.origin);

		const isLocalhost =
			window.location.hostname === "localhost" ||
			window.location.hostname === "127.0.0.1";

		if (hasHistory || (isLocalhost && !document.referrer)) {
			window.history.back();
			return;
		}

		this.#jumpToClosestParent(window.location.pathname);
	}

	#navigo;
	mountRoute;
	routeContext: RouteContext | undefined;

	constructor(args: RouterParams) {
		this.#navigo = new Navigo(args.rootPath);
		this.#navigo.notFound(args.notFoundHandler);
		this.mountRoute = args.mountRoute;
	}
}

export interface Route extends ViewComponent {
	onLeave?: () => void;
	refresh?: (match: Match) => Promise<void>;
}

export interface RouteContext {
	api: Api;
	router: Router;
	sp2gw: Api;
}

interface RouterParams {
	mountRoute: (childDom: ChildDom) => void;
	notFoundHandler: Handler;
	rootPath: string;
}

type RouteConstructor = new (ctx: RouteContext) => Route;
